import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';

/**
 * HotStreaks Component - Displays current hot streaks
 * Can be used on any page to show recent performance
 * 
 * @param {boolean} compact - Use smaller display for sidebars/smaller areas
 * @param {string} className - Additional CSS classes
 */
const HotStreaks = ({ compact = false, className = "" }) => {
  const [streakData, setStreakData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStreaks = async () => {
      try {
        const response = await axios.get(`${API}/stats/streak`);
        setStreakData(response.data);
      } catch (error) {
        console.warn('HotStreaks: Failed to fetch streaks:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchStreaks();
  }, []);

  const getDisplayableStreaks = () => {
    if (!streakData) return [];
    
    const streaks = [];
    
    if (streakData.hot_streaks && streakData.hot_streaks.length > 0) {
      streaks.push(...streakData.hot_streaks);
    }
    
    if (streaks.length === 0 && streakData.overall && streakData.overall.win_rate >= 55) {
      streaks.push(streakData.overall);
    }
    
    return streaks.slice(0, 6);
  };

  const displayStreaks = getDisplayableStreaks();

  const formatProfit = (profit) => {
    if (profit >= 0) return `+$${profit.toLocaleString()}`;
    return `-$${Math.abs(profit).toLocaleString()}`;
  };

  if (loading) {
    return (
      <div className={`${className}`}>
        <div className="flex justify-center gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-hsl-surface/50 rounded-lg p-3 animate-pulse w-40 h-16" />
          ))}
        </div>
      </div>
    );
  }

  if (displayStreaks.length === 0) return null;

  // Ultra compact - single line for tight spaces
  if (compact === 'mini') {
    return (
      <div className={`flex items-center justify-center gap-2 text-sm ${className}`}>
        <span className="text-orange-400">🔥</span>
        {displayStreaks.slice(0, 2).map((streak) => (
          <span key={streak.label} className="text-zinc-300">
            <span className="text-zinc-500">{streak.label}:</span>{' '}
            <span className="font-mono font-bold text-green-400">{streak.wins}-{streak.losses}</span>
            {streak !== displayStreaks.slice(0, 2).at(-1) && <span className="text-zinc-600 mx-2">|</span>}
          </span>
        ))}
      </div>
    );
  }

  // Compact version
  if (compact) {
    return (
      <div className={`${className}`}>
        <h3 className="text-center font-heading text-base font-bold uppercase text-white mb-3 flex items-center justify-center gap-2">
          <span className="text-orange-400">🔥</span> HSL Hot Streaks
        </h3>
        <div className="flex flex-wrap justify-center gap-2">
          {displayStreaks.map((streak) => (
            <div
              key={streak.label}
              className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${
                streak.is_hot 
                  ? 'bg-gradient-to-r from-orange-500/10 to-red-500/10 border-orange-500/30' 
                  : 'bg-zinc-800/50 border-zinc-700'
              }`}
            >
              {streak.is_hot && <span>🔥</span>}
              <div>
                <p className="text-zinc-400 text-xs">{streak.label}</p>
                <p className={`font-mono font-bold ${streak.is_hot ? 'text-orange-400' : 'text-green-400'}`}>
                  {streak.wins}-{streak.losses} <span className="text-green-400">{formatProfit(streak.profit)}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Full version
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={className}
    >
      <h3 className="text-center font-heading text-xl font-bold uppercase text-white mb-5 flex items-center justify-center gap-2">
        <motion.span 
          className="text-2xl"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
        >
          🔥
        </motion.span>
        <span className="bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent">
          HSL Hot Streaks
        </span>
        <motion.span 
          className="text-2xl"
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 1, repeat: Infinity, repeatDelay: 2 }}
        >
          🔥
        </motion.span>
      </h3>
      
      <div className="flex flex-wrap justify-center gap-4">
        {displayStreaks.map((streak) => (
          <motion.div
            key={streak.label}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 * displayStreaks.indexOf(streak) }}
            className={`inline-flex items-center gap-3 px-5 py-3 rounded-xl border ${
              streak.is_hot 
                ? 'bg-gradient-to-r from-orange-500/20 to-red-500/20 border-orange-500/50' 
                : streak.win_rate >= 60
                ? 'bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30'
                : 'bg-zinc-800/50 border-zinc-700'
            }`}
          >
            {streak.is_hot && <span className="text-2xl">🔥</span>}
            
            <div>
              <p className="text-zinc-400 text-xs uppercase tracking-wide">
                {streak.label || 'Overall'}
              </p>
              <p className={`font-mono text-xl font-black ${
                streak.is_hot ? 'text-orange-400' : streak.win_rate >= 60 ? 'text-green-400' : 'text-white'
              }`}>
                {streak.wins}-{streak.losses}
              </p>
            </div>
            
            <div className="border-l border-white/10 pl-3">
              <p className="text-zinc-500 text-xs">Profit</p>
              <p className={`font-mono text-lg font-bold ${streak.profit >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {formatProfit(streak.profit)}
              </p>
            </div>
            
            <div className="border-l border-white/10 pl-3 text-center">
              <p className={`font-mono font-bold ${streak.win_rate >= 70 ? 'text-green-400' : 'text-cyan-400'}`}>
                {streak.win_rate}%
              </p>
              <p className="text-zinc-500 text-xs">Last {streak.n}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default HotStreaks;
